import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import ModalLoadingOverlay from "./ModalLoadingOverlay";
import { getApiErrorMessage } from "../config/toastHelpers";
import type { SellPayload } from "../services/sellService";
import { userService } from "../services/services";
import { authStorage } from "../lib/authStorage";
import { getUserDisplayName, getUserPhone, getUserPincode, getUserAddress, getUserCity, getUserState } from "../lib/userDetails";
import { ModalFrame } from "../features/trade/components/ModalFrame";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { UserSummaryCard } from "../features/trade/components/UserSummaryCard";
import { WarehouseAddressCard } from "../features/trade/components/WarehouseAddressCard";
type SellModalProps = {
  isOpen: boolean;
  onClose: () => void;
  sellPayload?: SellPayload;
  onBack?: () => void;
};

export default function SellModal({
  isOpen,
  onClose,
  sellPayload,
  onBack,
}: SellModalProps) {
  const t = useTranslations('modals');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { userDetails, loading: loadingUser } = useCurrentUser();


  useEffect(() => {
    if (!isOpen) return;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    setError("");
    setLoading(false);
  }, [isOpen]);

  if (!isOpen) return null;

  const senderName = getUserDisplayName(userDetails);

  const handleSampleRequest = async () => {
    setError("");
    setLoading(true);
    try {
      if (!authStorage.hasSession()) {
        throw new Error("User not authenticated. Please login.");
      }
      if (!sellPayload) throw new Error("Missing sell payload.");
      await userService.createSample({
        productId: sellPayload.productId,
        operation: sellPayload.operation,
        recipientName: "Agarmart",
        streetAddress: getUserAddress(userDetails),
        apartment: userDetails?.apartment || undefined,
        city: getUserCity(userDetails),
        state: getUserState(userDetails),
        pincode: getUserPincode(userDetails),
        fullName: sellPayload.fullName,
        phoneNumber: sellPayload.phone,
        senderName: sellPayload.fullName,
      });
      onClose();
    } catch (submitError: unknown) {
      setError(getApiErrorMessage(submitError, "Failed to submit sample request."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalFrame panelClassName="relative flex w-full max-w-md max-h-[92dvh] flex-col rounded-xl bg-white shadow-xl">
      {(loading || loadingUser) && (
        <ModalLoadingOverlay
          message={loadingUser ? t('loadingDetails') : t('sell.submittingSample')}
        />
      )}

      <div className="sticky top-0 z-20 flex items-center justify-between rounded-t-xl bg-[#0f172a] px-5 py-5 shrink-0">
        <h2 className="flex items-center gap-2 text-lg font-bold text-white">
          {t('almostThere')}
        </h2>

        <button
          onClick={() => {
            if (!loading && !loadingUser) onClose();
          }}
          disabled={loading || loadingUser}
          className="text-gray-400 hover:text-white"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className="h-5 w-5 text-gray-400"
          >
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto p-6">
        <div className="space-y-6">
          {userDetails && (
            <UserSummaryCard
              title={`${t("yourDetails")}`}
              className="mb-4"
              items={[
                { label: t('labels.name'), value: sellPayload?.fullName || "-" },
                { label: t('labels.city'), value: getUserCity(userDetails) || "-" },
                { label: t('labels.address'), value: getUserAddress(userDetails) || "-" },
                { label: t('labels.phone'), value: sellPayload?.phone || "-" },
                { label: t('labels.pinCode'), value: getUserPincode(userDetails) || "-" },
              ]}
            />
          )}

          <p className="text-center text-gray-600">
            {t('sell.senderText')}
          </p>
          <WarehouseAddressCard
            title={t('warehouseAddress')}
            name="AgarMart HQ"
            addressLines={["10-ts lohamandi, Agarsen Square,", "Indore, Madhya Pradesh"]}
            mapHref="https://maps.google.com/?q=10-ts+lohamandi+Agarsen+Square+Indore+Madhya+Pradesh"
          />

          <div className="flex gap-2 rounded-xl border border-yellow-200 bg-yellow-50 p-4 mt-4">
            <p className="text-sm text-yellow-800">
              {t('sell.dispatchWarning')}
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <button
              onClick={handleSampleRequest}
              disabled={loading || loadingUser}
              className="w-full rounded-xl bg-blue-500 py-3 font-bold text-white disabled:opacity-60 hover:bg-blue-600"
            >
              {loading ? t('submitting') : t('sendSample')}
            </button>

            {error && <div className="mt-2 text-center text-sm text-red-600">{error}</div>}

            <button
              onClick={() => {
                if (loading || loadingUser) return;
                setError("");
                if (onBack) { onBack(); } else { onClose(); }
              }}
              disabled={loading || loadingUser}
              className="text-sm text-gray-500 hover:text-black"
            >
              {t('back')}
            </button>
          </div>
        </div>
      </div>
    </ModalFrame>
  );
}
